/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * Story Context
 *
 * Manages the state of interactive overlays (system chapters and technical appendices)
 * within the story experience. Allows users to explore detailed content without
 * losing their place in the narrative spine.
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

export type OverlayType = 'system-chapter' | 'technical-appendix' | null;

export interface OverlayState {
  type: OverlayType;
  id: string | null;
}

interface StoryContextValue {
  /** Current overlay state */
  overlay: OverlayState;
  /** Open a system chapter by option ID */
  openSystemChapter: (optionId: string) => void;
  /** Open a technical appendix by topic ID */
  openTechnicalAppendix: (appendixId: string) => void;
  /** Close any open overlay */
  closeOverlay: () => void;
  /** Check if any overlay is open */
  isOverlayOpen: boolean;
}

const StoryContext = createContext<StoryContextValue | null>(null);

interface StoryProviderProps {
  children: ReactNode;
}

export function StoryProvider({ children }: StoryProviderProps) {
  const [overlay, setOverlay] = useState<OverlayState>({ type: null, id: null });

  const openSystemChapter = useCallback((optionId: string) => {
    setOverlay({ type: 'system-chapter', id: optionId });
  }, []);

  const openTechnicalAppendix = useCallback((appendixId: string) => {
    setOverlay({ type: 'technical-appendix', id: appendixId });
  }, []);

  const closeOverlay = useCallback(() => {
    setOverlay({ type: null, id: null });
  }, []);

  const isOverlayOpen = overlay.type !== null;

  return (
    <StoryContext.Provider
      value={{
        overlay,
        openSystemChapter,
        openTechnicalAppendix,
        closeOverlay,
        isOverlayOpen,
      }}
    >
      {children}
    </StoryContext.Provider>
  );
}

export function useStoryContext(): StoryContextValue {
  const context = useContext(StoryContext);
  if (!context) {
    throw new Error('useStoryContext must be used within a StoryProvider');
  }
  return context;
}
